import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const inIframe = window.parent !== window;

  /**
   * fetchProfile — gọi /auth/me bằng token trong localStorage.
   *
   * Iframe mode:
   *   - Không có token → giữ loading=true, KHÔNG setLoading(false)
   *     (CrmTokenReceiver đang chạy song song, sẽ set token rồi gọi lại fetchProfile)
   *   - Có token → fetch bình thường
   *
   * Standalone mode:
   *   - Không có token → setLoading(false) → ProtectedRoute redirect /login
   */
  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      setLoading(false); // Release loading immediately — CrmTokenReceiver runs in parallel
      return;
    }

    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setLoading(false);
    } catch (error) {
      console.error('[AuthContext] fetchProfile error:', error);
      if (inIframe) {
        // Token hỏng → xóa, CrmTokenReceiver tự động xin token mới từ Portal
        localStorage.removeItem('token');
        localStorage.removeItem('_last_portal_token');
        // Giữ loading=true, chờ token mới
      } else {
        localStorage.removeItem('token');
        setLoading(false);
      }
    }
  }, [inIframe]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const loginWithGoogle = async (credential) => {
    await authService.googleLogin(credential);
    const userData = await authService.getCurrentUser();
    setUser(userData);
  };

  const login = async (email, password) => {
    try {
      await authService.login(email, password);
      const userData = await authService.getCurrentUser();
      setUser(userData);
      return true;
    } catch (error) {
      console.error('Login failed', error);
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    if (!inIframe) {
      window.location.href = '/login';
    }
  };

  // ⚠️ QUAN TRỌNG: Luôn render children — không gating sau loading
  // CrmTokenReceiver là children, nếu gating thì nó không bao giờ chạy được!
  // ProtectedRoute và các route sẽ tự xử lý trạng thái loading.

  // ---- Permission helpers ----
  // user.site_permissions = [{ site_id, site_slug, site_name, allowed_document_types[] }]

  /** Sites the current user has access to */
  const userSites = user?.site_permissions ?? [];

  /**
   * Allowed document types for a given site slug.
   * Superusers get all types; pass null/undefined for union across all sites.
   */
  /**
   * Allowed document types for a site.
   *
   * Superusers may use every type, but the list of types is no longer fixed in
   * code — it lives in the document_types catalog. Callers therefore pass the
   * catalog keys (from useDocumentTypes) as `allTypes`; hardcoding them here
   * would silently hide any type an admin adds.
   */
  const allowedDocTypes = (siteSlug, allTypes = null) => {
    if (user?.is_superuser) return allTypes ?? [];
    if (!siteSlug) {
      // Union across all sites
      const types = new Set();
      (user?.site_permissions ?? []).forEach((p) => p.allowed_document_types.forEach((t) => types.add(t)));
      return [...types];
    }
    const perm = (user?.site_permissions ?? []).find((p) => p.site_slug === siteSlug);
    return perm?.allowed_document_types ?? [];
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithGoogle, logout, loading, fetchProfile, userSites, allowedDocTypes }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
